import api, { ApiResponse } from './api';
import { Capability, RoleName, UserRole } from '../types/auth';

export interface User {
  id: number;
  name: string;
  email: string;
  username: string;
  user_type: UserRole;
  building_id: number | null;
  role?: RoleName;
  capabilities?: Capability[];
  assigned_building_ids?: number[];
  image?: string;
  image_url?: string;
  accessToken?: string;
}

export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  username?: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  device_id: string;
  device_token?: string;
  platform: number; // 1 = iOS, 2 = Android, 3 = Web
}

export interface LoginResponse {
  id: number;
  name: string;
  email: string;
  username: string;
  user_type: UserRole;
  building_id: number | null;
  role?: RoleName;
  capabilities?: Capability[];
  assigned_building_ids?: number[];
  accessToken: string;
}

export const authService = {
  async login(email: string, password: string): Promise<LoginResponse> {
    const payload: LoginRequest = {
      email,
      password,
      device_id: 'web-admin-' + Date.now(),
      device_token: '',
      platform: 3, // Web
    };

    let response;
    try {
      response = await api.post<ApiResponse<LoginResponse>>('/login-web', payload);
    } catch {
      // Network errors or 5xx server errors (rejected by validateStatus)
      throw new Error('Server error. Please try again later.');
    }

    // Handle 403 - access denied (passes through validateStatus since 403 < 500)
    if (response.status === 403) {
      throw new Error(response.data?.MESSAGE || 'Access denied. Admin privileges required.');
    }

    if (response.data.CODE === 200) {
      const loginUser = response.data.RESULT;
      // Store token and user in localStorage
      localStorage.setItem('accessToken', loginUser.accessToken);

      let user: LoginResponse = loginUser;
      try {
        const currentUser = await this.getMe();
        user = { ...loginUser, ...currentUser, accessToken: loginUser.accessToken };
      } catch {
        // Keep the login response usable during a rolling backend deployment.
      }

      localStorage.setItem('user', JSON.stringify(user));
      return user;
    }

    throw new Error(response.data.MESSAGE || 'Invalid email or password.');
  },

  async logout(): Promise<void> {
    try {
      await api.post('/logout');
    } catch (error) {
      // Even if API call fails, clear local storage
      console.error('Logout API error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
    }
  },

  async forgotPassword(email: string): Promise<void> {
    let response;
    try {
      response = await api.post<ApiResponse>('/forgot-password', { email });
    } catch {
      throw new Error('The reset service is temporarily unavailable. Please try again later.');
    }

    if (response.status === 429) {
      throw new Error('Too many reset requests. Please wait before trying again.');
    }

    if (response.data.CODE !== 200) {
      throw new Error('Unable to request a reset link. Please try again.');
    }
  },

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  },

  async getMe(): Promise<User> {
    const response = await api.get<ApiResponse<User>>('/me');

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to load your account');
  },

  async updateProfile(userId: number, data: UpdateProfileRequest): Promise<User> {
    const response = await api.put<ApiResponse<User>>(`/update-user/${userId}`, data);

    if (response.data.CODE === 200) {
      // Update stored user data
      const currentUser = this.getCurrentUser();
      if (currentUser) {
        const updatedUser = { ...currentUser, ...data };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return updatedUser;
      }
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to update profile');
  },

  async uploadProfileImage(userId: number, file: File): Promise<User> {
    const formData = new FormData();
    formData.append('image', file);

    const response = await api.post<ApiResponse<User>>(
      `/update-user/${userId}/image`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    if (response.data.CODE === 200) {
      const currentUser = this.getCurrentUser();
      if (currentUser) {
        const updatedUser = { ...currentUser, image: response.data.RESULT.image_url || response.data.RESULT.image };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return updatedUser;
      }
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to upload image');
  },

  async changePassword(data: ChangePasswordRequest): Promise<void> {
    const response = await api.post<ApiResponse>('/change-password', data);

    if (response.data.CODE !== 200) {
      throw new Error(response.data.MESSAGE || 'Failed to change password');
    }
  },
};

export default authService;
